import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// Import polyfills
// No polyfills needed here
import { StrictMode } from 'react';
import '@ssttevee/cfw-formdata-polyfill';
import * as Sentry from '@sentry/react';
import clientEnv from './env';
import { installChunkLoadAutoReload, tryRecoverStaleChunkError } from './utils/chunk-load-reload.js';
// Drop legacy service workers (previous sw.js cached HTML); unregister on each production load.
if (typeof window !== 'undefined' &&
    clientEnv.NODE_ENV === 'production' &&
    'serviceWorker' in navigator) {
    navigator.serviceWorker.getRegistrations().then((regs) => {
        regs.forEach((reg) => void reg.unregister());
    });
}
// One automatic reload when hashed JS/CSS chunks are missing after a deploy (avoids infinite loops).
installChunkLoadAutoReload();
// Setup fetch override early to prevent tldraw translation errors
import './utils/fetch-override';
// React and Redux imports
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { BrowserRouter } from 'react-router-dom';
import App from './components/App';
import store, { persistor } from './redux/store';
// Style imports
import './styles/theme.css';
import './index.css';
import './styles/pdf-transparent.css';
import './styles/reference-book-transparent.css';
// Initialize error handling for external scripts
import './utils/external-error-handler';
// Initialize i18n (synchronous - needed for useTranslation before first render)
import './config/i18n';
// Sentry initializes only after analytics consent — see `utils/sentry-init.ts` and CookieConsentBanner.
// Supabase logger is disabled to avoid chunk loading issues
// If needed, it can be re-enabled with a regular import
const rootElement = document.getElementById('root');
if (!rootElement) {
    throw new Error('Root element not found');
}
// Hide the initial loading screen once React is about to render
const hideLoadingScreen = () => {
    const loadingEl = document.getElementById('initial-loading');
    if (loadingEl) {
        loadingEl.style.opacity = '0';
        loadingEl.style.transition = 'opacity 0.5s ease-out';
        setTimeout(() => {
            loadingEl.style.display = 'none';
        }, 500);
    }
};
const root = ReactDOM.createRoot(rootElement);
root.render(_jsx(StrictMode, { children: _jsx(Sentry.ErrorBoundary, { onError: (error) => {
            tryRecoverStaleChunkError(error);
        }, fallback: ({ error, resetError }) => {
            const isDevelopment = clientEnv.NODE_ENV === 'development';
            return (_jsxs("div", { style: {
                    padding: '20px',
                    textAlign: 'center',
                    fontFamily: 'Arial, sans-serif',
                    backgroundColor: '#f8f9fa',
                    minHeight: '100vh',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center'
                }, children: [_jsx("h2", { style: { color: '#dc3545', marginBottom: '20px' }, children: "Oops! Something went wrong" }), _jsx("p", { style: { marginBottom: '20px', color: '#6c757d' }, children: "We're sorry, but something unexpected happened. Our team has been notified." }), isDevelopment && error instanceof Error && (_jsxs("details", { style: {
                            marginBottom: '20px',
                            padding: '10px',
                            backgroundColor: '#e9ecef',
                            borderRadius: '4px',
                            textAlign: 'left',
                            maxWidth: '600px',
                            whiteSpace: 'pre-wrap'
                        }, children: [_jsx("summary", { style: { cursor: 'pointer', fontWeight: 'bold' }, children: "Technical Details (Development Only)" }), _jsxs("pre", { style: { fontSize: '12px', overflow: 'auto' }, children: [error.toString(), error.stack && `\n\nStack trace:\n${error.stack}`] })] })), _jsx("button", { onClick: resetError, style: {
                            padding: '10px 20px',
                            backgroundColor: '#007bff',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '16px'
                        }, children: "Try Again" })] }));
        }, showDialog: clientEnv.NODE_ENV === 'development', children: _jsx(Provider, { store: store, children: _jsx(PersistGate, { loading: _jsx("div", { style: {
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: '100vh',
                        fontFamily: 'system-ui, sans-serif'
                    }, children: _jsx("div", { children: "Loading..." }) }), persistor: persistor, onBeforeLift: () => {
                    // Hide loading screen once persistence is hydrated
                    hideLoadingScreen();
                }, children: _jsx(BrowserRouter, { children: _jsx(App, {}) }) }) }) }) }));
// Fallback: Hide loading screen after React renders
// This ensures the loading screen disappears even if PersistGate doesn't trigger
setTimeout(() => {
    hideLoadingScreen();
}, 1000);
